/**
 * 請求書の一覧の まとめて発行・まとめて PDF・まとめてメール（#267 3c、docs/97 決めごと13・§8 の5）。門は sales（両製品）・PC だけ（スマホは出さない）。
 *   一覧で選ぶと表の上に帯（n枚 選択・下書き n・発行済 n｜選択解除・まとめて発行・まとめて PDF・まとめてメール）。
 *   どれも窓で確かめてから。並びは 請求日の古い順・同じ日は一覧の並び（bulkOrder）＝窓の上から順に番号が付き、API も同じ決まりで並べ直す。
 *   発行は1枚ずつ（今の発行の口と同じ守り）。出せない1枚で止め、出た分は出たまま＝窓に 結果 の列を出して残す。
 *   メールは 1枚ずつ送るときの初めの値（宛先・件名・本文・渡し方）で1通ずつ。宛先なし・下書きは送らない
 */
import {
  INVOICE_BULK_LIMITS,
  INVOICE_BULK_MSG as M,
  MAIL_QUEUE_MSG,
  type InvoiceBulkIssueResult,
  type InvoiceBulkMailPreview,
  type InvoiceBulkMailResult,
  type InvoiceBulkPdfFormat,
  type InvoiceSummary,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api, fetchFile, messageOf } from "../api.js";
import { Segmented } from "../components/Segmented.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";
import { mdSlash, yen } from "../lib/num.js";

/** 窓と API に渡す並び＝請求日の古い順・同じ日は一覧の並び（API の rowsOf も 請求日 → 送った順 で並べ直す） */
export const bulkOrder = (xs: InvoiceSummary[]): InvoiceSummary[] =>
  xs
    .map((x, i) => [x, i] as const)
    .sort((a, b) => a[0].issueDate.localeCompare(b[0].issueDate) || a[1] - b[1])
    .map(([x]) => x);

type Kind = "issue" | "pdf" | "mail";

const refresh = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ["invoices"] });
  void qc.invalidateQueries({ queryKey: ["invoice"] });
  void qc.invalidateQueries({ queryKey: ["home"] });
  void qc.invalidateQueries({ queryKey: ["closing"] });
  void qc.invalidateQueries({ queryKey: ["deliveries"] });
  void qc.invalidateQueries({ queryKey: ["mail-queue"] });
};

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** 選んだときの帯（表の上）と窓 */
export function InvoiceBulkBand({ picked, onClear }: { picked: InvoiceSummary[]; onClear: () => void }) {
  const [open, setOpen] = useState<Kind | null>(null);
  const rows = bulkOrder(picked);
  const drafts = rows.filter((x) => x.status === "draft").length;
  const issued = rows.length - drafts;
  const over = (k: Kind) => rows.length > INVOICE_BULK_LIMITS[k];
  const overText = over("issue") ? M.overLimit(INVOICE_BULK_LIMITS.issue) : over("mail") ? M.mailOverLimit(INVOICE_BULK_LIMITS.mail) : "";
  return (
    <>
      <div className="cl-bulk" data-testid="invoice-bulk">
        <b data-testid="invoice-bulk-count">{M.selected(rows.length)}</b>
        <span data-testid="invoice-bulk-breakdown">{M.breakdown(drafts, issued)}</span>
        {overText ? <span className="shu" data-testid="invoice-bulk-over">{overText}</span> : null}
        <span className="sp" />
        <button type="button" className="btn text" onClick={onClear} data-testid="invoice-bulk-deselect">
          {M.deselect}
        </button>
        <button type="button" className={drafts ? "btn primary" : "btn"} disabled={!drafts || over("issue")} onClick={() => setOpen("issue")} data-testid="invoice-bulk-issue">
          {M.issue}
        </button>
        <button type="button" className="btn" disabled={over("pdf")} onClick={() => setOpen("pdf")} data-testid="invoice-bulk-pdf">
          {M.pdf}
        </button>
        <button type="button" className="btn" disabled={!issued || over("mail")} onClick={() => setOpen("mail")} data-testid="invoice-bulk-mail">
          {M.mail}
        </button>
      </div>
      {open === "issue" ? <IssueDialog rows={rows.filter((x) => x.status === "draft")} onClose={() => setOpen(null)} /> : null}
      {open === "pdf" ? <PdfDialog rows={rows} onClose={() => setOpen(null)} /> : null}
      {open === "mail" ? <MailDialog rows={rows} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** 窓の枠（.dim ＞ .dlg）。Esc と後ろで閉じる（送っている最中は閉じない） */
function BulkDialog({ title, help, testId, busy, onClose, children, foot }: {
  title: string;
  help?: string | undefined;
  testId: string;
  busy: boolean;
  onClose: () => void;
  children: ReactNode;
  foot: ReactNode;
}) {
  const close = () => !busy && onClose();
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg wide ib-dlg" role="dialog" aria-modal="true" aria-label={title} data-testid={testId} onKeyDown={(e) => e.key === "Escape" && close()}>
        <div className="dh">
          {title}
          {help ? <Help testId={`${testId}-help`} label={`${title}の説明`} text={help} /> : null}
        </div>
        <div className="db">{children}</div>
        <div className="df">{foot}</div>
      </div>
    </div>
  );
}

/** 請求書の短い表（請求日・{customer}・件名・金額・番号か結果） */
function BulkTable({ rows, last, lastHead, testId }: { rows: InvoiceSummary[]; last: (x: InvoiceSummary) => ReactNode; lastHead: string; testId: string }) {
  return (
    <div className="ib-scroll">
      <table className="list ib-table" data-testid={testId}>
        <thead>
          <tr>
            <th>{M.colDate}</th>
            <th>{t(M.colClient)}</th>
            <th>{M.colSubject}</th>
            <th className="amt">{M.colTotal}</th>
            <th>{lastHead}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id} data-testid={`${testId}-row`}>
              <td className="num">{mdSlash(x.issueDate)}</td>
              <td>
                <b>{x.clientName}</b>
              </td>
              <td>{x.subject || <span className="muted">—</span>}</td>
              <td className="amt num">{yen(x.total)}</td>
              <td data-testid={`${testId}-last`}>{last(x)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IssueDialog({ rows: given, onClose }: { rows: InvoiceSummary[]; onClose: () => void }) {
  // 開いたときの並びのまま（発行・送信のあと一覧を読み直しても、窓の行は変えない＝結果の列を残す）
  const [rows] = useState(given);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [res, setRes] = useState<InvoiceBulkIssueResult | null>(null);
  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api<InvoiceBulkIssueResult>("POST", "/invoices/bulk-issue", { ids: rows.map((x) => x.id) });
      refresh(qc);
      if (!r.failed) {
        toast(M.issued(r.issued.length), undefined, "success", "invoice-bulk-issued");
        onClose();
        return;
      }
      setRes(r);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const no = new Map(res?.issued.map((x) => [x.id, x.documentNo]) ?? []);
  return (
    <BulkDialog
      title={M.issue}
      help={t(M.issueHelp)}
      testId="invoice-bulk-issue-dialog"
      busy={busy}
      onClose={onClose}
      foot={
        res ? (
          <>
            <span />
            <button type="button" className="btn primary" autoFocus onClick={onClose} data-testid="invoice-bulk-issue-close">
              閉じる
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn" disabled={busy} onClick={onClose} data-testid="invoice-bulk-issue-cancel">
              取消
            </button>
            <button type="button" className="btn primary" autoFocus disabled={busy} onClick={() => void run()} data-testid="invoice-bulk-issue-confirm">
              {M.issueBtn}（{rows.length}枚）
            </button>
          </>
        )
      }
    >
      {res ? (
        <div data-testid="invoice-bulk-issue-result">
          <p style={{ margin: 0 }}>{M.issued(res.issued.length)}</p>
          {res.failed ? (
            <p className="shu" style={{ margin: 0 }} data-testid="invoice-bulk-issue-failed">
              {M.issueStopped(res.failed.clientName, res.failed.message)}
            </p>
          ) : null}
          {res.notDone.length ? <p style={{ margin: 0 }}>{M.notDone(res.notDone.length)}</p> : null}
        </div>
      ) : (
        <p style={{ margin: 0 }}>{M.issueLead}</p>
      )}
      <BulkTable
        rows={rows}
        testId="invoice-bulk-issue-list"
        lastHead={res ? M.colResult : M.colNo}
        last={(x) =>
          res ? (
            no.get(x.id) ? <span className="num">{no.get(x.id)}</span>
            : res.failed?.id === x.id ? <span className="shu">{M.resultStopped}</span>
            : <span className="muted">{M.resultNotDone}</span>
          ) : (
            <span className="muted">未発行</span>
          )
        }
      />
      <ErrorText>{error}</ErrorText>
    </BulkDialog>
  );
}

function PdfDialog({ rows: given, onClose }: { rows: InvoiceSummary[]; onClose: () => void }) {
  // 開いたときの並びのまま（発行・送信のあと一覧を読み直しても、窓の行は変えない＝結果の列を残す）
  const [rows] = useState(given);
  const [fmt, setFmt] = useState<InvoiceBulkPdfFormat>("merged");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const drafts = rows.filter((x) => x.status === "draft").length;
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const blob = await fetchFile("/api/v1/invoices/bulk-pdf", { method: "POST", body: { ids: rows.map((x) => x.id), format: fmt } });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `請求書_${today()}_${rows.length}枚.${fmt === "zip" ? "zip" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 1000);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <BulkDialog
      title={M.pdf}
      testId="invoice-bulk-pdf-dialog"
      busy={busy}
      onClose={onClose}
      foot={
        <>
          <button type="button" className="btn" disabled={busy} onClick={onClose} data-testid="invoice-bulk-pdf-cancel">
            取消
          </button>
          <button type="button" className="btn primary" autoFocus disabled={busy} onClick={() => void save()} data-testid="invoice-bulk-pdf-confirm">
            {busy ? M.pdfMaking : M.pdfBtn}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{M.pdfLead}</p>
      <div>
        <Segmented<InvoiceBulkPdfFormat>
          label="保存の形"
          value={fmt}
          onChange={setFmt}
          disabled={busy}
          testId="invoice-bulk-pdf-format"
          options={[
            { value: "merged", label: M.formatMerged, testId: "invoice-bulk-pdf-merged" },
            { value: "zip", label: M.formatZip, testId: "invoice-bulk-pdf-zip" },
          ]}
        />
      </div>
      {drafts ? <p className="muted" style={{ margin: 0 }}>{M.pdfDraftNote(drafts)}</p> : null}
      <BulkTable rows={rows} testId="invoice-bulk-pdf-list" lastHead={M.colNo} last={(x) => (x.documentNo ? <span className="num">{x.documentNo}</span> : <span className="muted">未発行</span>)} />
      <ErrorText>{error}</ErrorText>
    </BulkDialog>
  );
}

function MailDialog({ rows: given, onClose }: { rows: InvoiceSummary[]; onClose: () => void }) {
  // 開いたときの並びのまま（発行・送信のあと一覧を読み直しても、窓の行は変えない＝結果の列を残す）
  const [rows] = useState(given);
  const qc = useQueryClient();
  const { toast } = useToast();
  const ids = rows.map((x) => x.id);
  const pre = useQuery({
    queryKey: ["invoice-bulk-mail", ids],
    queryFn: () => api<InvoiceBulkMailPreview>("POST", "/invoices/bulk-mail/preview", { ids }),
    gcTime: 0,
    staleTime: 0,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [res, setRes] = useState<InvoiceBulkMailResult | null>(null);
  const p = pre.data;
  const ok = p ? p.rows.filter((r) => r.ok).length : 0;
  const left = p ? Math.max(0, p.perDay - p.sentToday) : 0;
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api<InvoiceBulkMailResult>("POST", "/invoices/bulk-mail", { ids });
      refresh(qc);
      if (!r.failed.length && !r.notDone.length) {
        toast(M.mailSent(r.sent.length), undefined, "success", "invoice-bulk-mailed");
        onClose();
        return;
      }
      setRes(r);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const byId = new Map(p?.rows.map((r) => [r.id, r]) ?? []);
  const sent = new Set(res?.sent.map((x) => x.id) ?? []);
  const failed = new Map(res?.failed.map((x) => [x.id, x]) ?? []);
  const queued = new Set(res?.queued ?? []);
  return (
    <BulkDialog
      title={M.mail}
      help={t(M.mailHelp)}
      testId="invoice-bulk-mail-dialog"
      busy={busy}
      onClose={onClose}
      foot={
        res ? (
          <>
            <span />
            <button type="button" className="btn primary" autoFocus onClick={onClose} data-testid="invoice-bulk-mail-close">
              閉じる
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn" disabled={busy} onClick={onClose} data-testid="invoice-bulk-mail-cancel">
              取消
            </button>
            <button type="button" className="btn primary" autoFocus disabled={busy || !p || !ok} onClick={() => void send()} data-testid="invoice-bulk-mail-confirm">
              {M.mailBtn}
            </button>
          </>
        )
      }
    >
      {res ? (
        <div data-testid="invoice-bulk-mail-result">
          <p style={{ margin: 0 }}>{M.mailSent(res.sent.length)}</p>
          {/* 全社の送る速さ・1日の上限で止めた：送れた分は記録のまま、残り（送れる行）は送信待ち＝枠が空き次第 API が1通ずつ送る（#268 1.5c-2、docs/98 §7） */}
          {(res.stoppedBy === "rate" || res.stoppedBy === "day") && res.queued?.length ? (
            <div data-testid="invoice-bulk-mail-queued">
              <p className="shu" style={{ margin: 0 }}>
                {(res.stoppedBy === "rate" ? M.mailRateQueued : M.mailDayQueued)(res.queued?.length ?? 0)}
              </p>
              <p style={{ margin: 0 }}>
                <Link to="/cover-letters?f=queue" data-testid="invoice-bulk-mail-queue-link">
                  {MAIL_QUEUE_MSG.toList}
                </Link>
              </p>
            </div>
          ) : res.stoppedBy === "rate" || res.stoppedBy === "day" ? (
            <p className="shu" style={{ margin: 0 }} data-testid="invoice-bulk-mail-rest">
              {(res.stoppedBy === "rate" ? M.mailRateRest : M.mailDayRest)(res.notDone.filter((id) => byId.get(id)?.ok).length)}
            </p>
          ) : null}
          {res.failed.map((f) => (
            <p key={f.id} className="shu" style={{ margin: 0 }} data-testid="invoice-bulk-mail-failed">
              {M.mailFailed(f.clientName, f.message)}
            </p>
          ))}
        </div>
      ) : (
        <p style={{ margin: 0 }}>{t(M.mailLead)}</p>
      )}
      {pre.isError ? <ErrorText>{messageOf(pre.error)}</ErrorText> : null}
      {/* 宛先が届くまでも同じ形（表は選んだ請求書から先に描き、宛先・渡し方の欄だけ「…」。#179） */}
      {!res ? (
        p && ok > left ? (
          <p className="shu" style={{ margin: 0 }} data-testid="invoice-bulk-mail-overday">
            {M.mailOverDay(left, ok - left)}
          </p>
        ) : (
          <p style={{ margin: 0 }} data-testid="invoice-bulk-mail-count">
            {p ? M.mailCount(ok, p.sentToday, p.perDay) : M.mailLoading}
          </p>
        )
      ) : null}
      <div className="ib-scroll">
        <table className="list ib-table" data-testid="invoice-bulk-mail-list">
          <thead>
            <tr>
              <th>{M.colNo}</th>
              <th>{t(M.colClient)}</th>
              <th className="amt">{M.colTotal}</th>
              <th>{M.colTo}</th>
              <th>{res ? M.colResult : M.colMode}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const r = byId.get(x.id);
              return (
                <tr key={x.id} data-testid="invoice-bulk-mail-list-row">
                  <td className="num">{x.documentNo ?? <span className="muted">未発行</span>}</td>
                  <td>
                    <b>{x.clientName}</b>
                  </td>
                  <td className="amt num">{yen(x.total)}</td>
                  <td className="ib-to" data-testid="invoice-bulk-mail-to">
                    {!r ? <span className="muted">…</span> : r.ok ? r.to.join("、") : <span className="muted">{r.reason ?? ""}</span>}
                  </td>
                  <td data-testid="invoice-bulk-mail-last">
                    {res ? (
                      sent.has(x.id) ? M.mailResultSent
                      : queued.has(x.id) ? <b>{M.mailResultQueued}</b>
                      : failed.has(x.id) ? <span className="shu">{M.mailResultFailed}</span>
                      : <span className="muted">{r?.ok ? M.mailResultNotDone : "—"}</span>
                    ) : !r ? (
                      <span className="muted">…</span>
                    ) : r.ok ? (
                      r.mode === "attach" ? M.mailModeAttach : M.mailModePage
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ErrorText>{error}</ErrorText>
    </BulkDialog>
  );
}
